import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/cn';
import type { Card } from '@/lib/qcp/protocol';
import { PlayingCard } from './PlayingCard';

interface DrawTrayProps {
  /** The cards currently in the player's draw hand for this round. */
  cards: Card[];
  /** Index of the selected card (null = none selected). */
  selectedIndex?: number | null;
  /** Called when the player taps a draw card. */
  onSelect?: (index: number) => void;
  /**
   * True when the only remaining card is this round's auto-discard (Pineapple:
   * 2 of 3 already placed). The tray then labels it as the discard and the card
   * is shown non-selectable with a red ring — instead of "Your draw".
   */
  discarding?: boolean;
}

/**
 * The player's draw tray — the set of cards dealt for this round, laid out
 * horizontally. Each card is tappable; the selected card gets a brand ring.
 *
 * In round 0 all 5 cards are shown (initial deal).
 * In rounds 1-4 the Pineapple draw-3 gives 3 cards; the tray renders exactly
 * what `cards` contains (the caller passes only the current draw).
 */
export function DrawTray({ cards, selectedIndex = null, onSelect, discarding = false }: DrawTrayProps) {
  const { t } = useTranslation('chinesepoker');
  if (cards.length === 0) return null;

  return (
    <div className="flex flex-col items-center gap-2">
      <span
        className={cn(
          'text-[11px] font-semibold uppercase tracking-widest',
          discarding ? 'text-danger' : 'text-fg-muted',
        )}
      >
        {discarding ? t('match.discarding') : t('match.yourDraw')}
      </span>
      <div className={cn('flex gap-2', cards.length <= 3 ? 'justify-center' : 'flex-wrap justify-center')}>
        {cards.map((card, idx) => (
          <PlayingCard
            key={idx}
            card={card}
            selected={!discarding && selectedIndex === idx}
            discard={discarding}
            onClick={discarding || !onSelect ? undefined : () => onSelect(idx)}
          />
        ))}
      </div>
    </div>
  );
}
